/*
 * ॐ नमः शिवाय
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * This software is licensed under the VIJAYA 1.0 License.
 *
 * You are free to use, modify, and distribute this software under the terms 
 * of the VIJAYA 1.0 License.
 *
 * MANDATORY ATTRIBUTION:
 * 1. The attribution to Rudra Coresoft Technologies Private Limited in this 
 *    copyright notice must not be removed even after modifications.
 * 2. For Free License usage, the footer phrases "Powered by arcee.io" and 
 *    "Developed by Rudra Coresoft" (plus logos) must remain visible.
 *
 * See the LICENSE file for the full text.
 */

/**
 * CreateApplicationUserComponent
 * - Full theme and layout
 * - All validations per Django model
 * - Password fields with show/hide toggle
 * - Checkbox for sending user's credentials via email
 * - Buttons stack vertically below 400px
 *
 * Props:
 *  - onSubmit(formData)
 *  - onCancel()
 *  - serverErrors: object mapping field -> error string (optional)
 */
import React, { useState, useEffect } from 'react';
import { useTheme } from '../../theme/ThemeContext';
import ThemedInput from '../common/ThemedInput';
import Button from '../Button';
import '../../styles/EdgeHacks.css';

/**
 * Regex for Django username: letters, digits, @/./+/-/_
 */
const USERNAME_REGEX = /^[\w.@+-]+$/;
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
// Additional password checks
const UPPER_REGEX = /[A-Z]/;
const DIGIT_REGEX = /\d/;
const SPECIAL_REGEX = /[!@#$%^&*(),.?":{}|<>]/;

function CreateApplicationUserComponent({ onSubmit, onCancel, serverErrors = {} }) {
    const { theme } = useTheme();

    // Form field state
    const [form, setForm] = useState({
        username: '',
        email: '',
        firstName: '',
        lastName: '',
        password: '',
        confirmPassword: '',
        sendMail: false
    });

    // Error state for validation (client + server)
    const [errors, setErrors] = useState({});

    // Field toggles for password/confirm password
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);

    // Responsive: stack action buttons if screen < 400px
    const [isMobileStack, setIsMobileStack] = useState(false);
    useEffect(() => {
        setIsMobileStack(window.innerWidth < 400);
        function handleResize() {
            setIsMobileStack(window.innerWidth < 400);
        }
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // Apply serverErrors into local errors with auto-clear (4s) similar to CreateEditRoleComponent
    useEffect(() => {
        if (!onSubmit) return;
        if (typeof serverErrors !== 'object' || serverErrors === null) return;

        const mapped = {};
        for (const [k, v] of Object.entries(serverErrors)) {
            if (Array.isArray(v)) mapped[k] = v.join(' ');
            else mapped[k] = String(v);
        }

        setErrors(prev => ({ ...prev, ...mapped }));

        const timers = [];
        Object.keys(mapped).forEach(field => {
            const t = setTimeout(() => {
                setErrors(prev => ({ ...prev, [field]: '' }));
            }, 4000);
            timers.push(t);
        });

        return () => timers.forEach(t => clearTimeout(t));
    }, [serverErrors, onSubmit]);

    /**
     * Generic input field and checkbox change handler
     */
    function handleChange(e) {
        const { name, value, type, checked } = e.target;
        setForm(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    }

    /**
     * Validates all fields, returns error object (client side only)
     */
    function validate(values) {
        let errs = {};

        // Username: required, allowed chars, max 150
        const uname = values.username.trim();
        if (!uname) {
            errs.username = 'Username is required.';
        } else if (!USERNAME_REGEX.test(uname)) {
            errs.username = 'Allowed: letters, digits, @/./+/-/_';
        } else if (uname.length > 150) {
            errs.username = 'Maximum length: 150 characters.';
        }

        // Email: required, valid format
        const email = values.email.trim();
        if (!email) {
            errs.email = 'Email is required.';
        } else if (!EMAIL_REGEX.test(email)) {
            errs.email = 'Enter a valid email address.';
        }

        // First name: not strictly required; max 30
        if (values.firstName && values.firstName.length > 30) {
            errs.firstName = 'First name can be max 30 characters.';
        }

        // Last name: not strictly required; max 150
        if (values.lastName && values.lastName.length > 150) {
            errs.lastName = 'Last name can be max 150 characters.';
        }

        // Password: required, min 8 chars, one uppercase, one digit, one special
        if (!values.password) {
            errs.password = 'Password is required.';
        } else if (values.password.length < 8) {
            errs.password = 'Password must be at least 8 characters.';
        } else if (!UPPER_REGEX.test(values.password) || !DIGIT_REGEX.test(values.password) || !SPECIAL_REGEX.test(values.password)) {
            errs.password = 'Password must contain at least 1 uppercase letter, 1 digit and 1 special character.';
        }

        // Confirm password: match password, required
        if (!values.confirmPassword) {
            errs.confirmPassword = 'Please confirm password.';
        } else if (values.confirmPassword !== values.password) {
            errs.confirmPassword = 'Passwords do not match.';
        }

        return errs;
    }

    /**
     * Handles submit: validate, trim, and call parent submit if valid
     */
    function handleSubmit(e) {
        e.preventDefault();
        const submitData = {
            ...form,
            username: form.username.trim(),
            email: form.email.trim(),
            firstName: form.firstName.trim(),
            lastName: form.lastName.trim(),
        };
        const vErrors = validate(submitData);
        setErrors(vErrors);
        if (Object.keys(vErrors).length === 0) {
            if (onSubmit) onSubmit(submitData);
        }
    }

    /**
     * On cancel: calls onCancel prop if provided, otherwise resets form
     */
    function handleCancel(e) {
        e && e.preventDefault();
        if (onCancel) return onCancel();
        setForm({
            username: '',
            email: '',
            firstName: '',
            lastName: '',
            password: '',
            confirmPassword: '',
            sendMail: false
        });
        setErrors({});
    }

    // -- Card layout for consistent sizing and shadow --
    const cardStyle = {
        maxWidth: '98vw',
        background: '#fff',
        borderRadius: theme.cardRadius,
        boxShadow: '0 6px 32px 0 rgba(80,80,180,0.10)',
        padding: '2.5em 2em 2em 2em',
        margin: '32px auto',
        position: 'relative'
    };

    // -- Button block responsive style --
    const buttonBlockStyle = {
        display: 'flex',
        flexDirection: isMobileStack ? 'column' : 'row',
        justifyContent: 'center',
        alignItems: 'center',
        gap: isMobileStack ? '0' : '14px',
        marginTop: '2.2rem'
    };

    return (
        <div className="d-flex flex-column align-items-center justify-content-center" style={{ minHeight: '80vh', background: '#f6f8fa' }}>
            <div style={cardStyle}>
                {/* Title */}
                <h3 className="fw-bold mb-4" style={{ color: theme.primary }}>
                    Create Application User
                </h3>
                <form onSubmit={handleSubmit} autoComplete="off">
                    {/* Username */}
                    <div className="mb-3">
                        <label htmlFor="username" className="form-label fw-semibold" style={{ color: theme.primary, fontSize: 15 }}>
                            Username
                        </label>
                        <ThemedInput
                            id="username"
                            name="username"
                            value={form.username}
                            error={!!errors.username}
                            onChange={handleChange}
                            maxLength={150}
                            autoComplete="username"
                            placeholder="Letters, numbers, @/./+/-/_"
                        />
                        <div style={{ fontSize: '0.9em', color: '#888' }}>
                            Max 150 characters. Use letters, digits and @/./+/-/_ only.
                        </div>
                        {errors.username && <div style={{ color: "#c80b23", fontSize: 13, marginTop: 4 }}>{errors.username}</div>}
                    </div>
                    {/* Email Address */}
                    <div className="mb-3">
                        <label htmlFor="email" className="form-label fw-semibold" style={{ color: theme.primary, fontSize: 15 }}>
                            Email Address
                        </label>
                        <ThemedInput
                            id="email"
                            name="email"
                            value={form.email}
                            error={!!errors.email}
                            onChange={handleChange}
                            maxLength={254}
                            autoComplete="email"
                            type="email"
                            placeholder="user@email.com"
                        />
                        {errors.email && <div style={{ color: "#c80b23", fontSize: 13, marginTop: 4 }}>{errors.email}</div>}
                    </div>
                    {/* First Name */}
                    <div className="mb-3">
                        <label htmlFor="firstName" className="form-label fw-semibold" style={{ color: theme.primary, fontSize: 15 }}>
                            First Name
                        </label>
                        <ThemedInput
                            id="firstName"
                            name="firstName"
                            value={form.firstName}
                            error={!!errors.firstName}
                            onChange={handleChange}
                            maxLength={30}
                            autoComplete="given-name"
                            placeholder="First Name"
                        />
                        {errors.firstName && <div style={{ color: "#c80b23", fontSize: 13, marginTop: 4 }}>{errors.firstName}</div>}
                    </div>
                    {/* Last Name */}
                    <div className="mb-3">
                        <label htmlFor="lastName" className="form-label fw-semibold" style={{ color: theme.primary, fontSize: 15 }}>
                            Last Name
                        </label>
                        <ThemedInput
                            id="lastName"
                            name="lastName"
                            value={form.lastName}
                            error={!!errors.lastName}
                            onChange={handleChange}
                            maxLength={150}
                            autoComplete="family-name"
                            placeholder="Last Name"
                        />
                        {errors.lastName && <div style={{ color: "#c80b23", fontSize: 13, marginTop: 4 }}>{errors.lastName}</div>}
                    </div>
                    {/* Password field + show/hide */}
                    <div className="mb-3">
                        <label htmlFor="password" className="form-label fw-semibold" style={{ color: theme.primary, fontSize: 15 }}>
                            Password
                        </label>
                        <div style={{ position: 'relative' }}>
                            <ThemedInput
                                id="password"
                                name="password"
                                className="no-edge-eye"
                                value={form.password}
                                error={!!errors.password}
                                onChange={handleChange}
                                type={showPassword ? "text" : "password"}
                                autoComplete="new-password"
                                placeholder="Password"
                            />
                            {/* Toggle visibility */}
                            <span
                                style={{
                                    position: 'absolute',
                                    right: 12,
                                    top: 8,
                                    cursor: 'pointer',
                                    color: theme.primary
                                }}
                                title={showPassword ? "Hide password" : "Show password"}
                                onClick={() => setShowPassword(v => !v)}
                                tabIndex={0}
                                aria-label={showPassword ? "Hide password" : "Show password"}
                            >
                                <i className={showPassword ? "bi bi-eye-slash" : "bi bi-eye"} />
                            </span>
                        </div>
                        <div style={{ fontSize: '0.9em', color: '#888' }}>
                            At least 8 characters, 1 uppercase, 1 digit, 1 special.
                        </div>
                        {errors.password && <div style={{ color: "#c80b23", fontSize: 13, marginTop: 4 }}>{errors.password}</div>}
                    </div>
                    {/* Confirm Password field + show/hide */}
                    <div className="mb-3">
                        <label htmlFor="confirmPassword" className="form-label fw-semibold" style={{ color: theme.primary, fontSize: 15 }}>
                            Confirm Password
                        </label>
                        <div style={{ position: 'relative' }}>
                            <ThemedInput
                                id="confirmPassword"
                                name="confirmPassword"
                                className="no-edge-eye"
                                value={form.confirmPassword}
                                error={!!errors.confirmPassword}
                                onChange={handleChange}
                                type={showConfirmPassword ? "text" : "password"}
                                autoComplete="new-password"
                            />
                            {/* Toggle visibility */}
                            <span
                                style={{
                                    position: 'absolute',
                                    right: 12,
                                    top: 8,
                                    cursor: 'pointer',
                                    color: theme.primary
                                }}
                                title={showConfirmPassword ? "Hide password" : "Show password"}
                                onClick={() => setShowConfirmPassword(v => !v)}
                                tabIndex={0}
                                aria-label={showConfirmPassword ? "Hide password" : "Show password"}
                            >
                                <i className={showConfirmPassword ? "bi bi-eye-slash" : "bi bi-eye"} />
                            </span>
                        </div>
                        {errors.confirmPassword && <div style={{ color: "#c80b23", fontSize: 13, marginTop: 4 }}>{errors.confirmPassword}</div>}
                    </div>
                    {/* Checkbox for sending login credentials */}
                    <div className="mb-4">
                        <label className="form-check-label" style={{ fontWeight: 500 }}>
                            <input
                                type="checkbox"
                                className="form-check-input me-2"
                                name="sendMail"
                                checked={form.sendMail}
                                onChange={handleChange}
                                style={{ accentColor: theme.primary, marginRight: 7 }}
                            />
                            Send mail to user's email address with login credentials
                        </label>
                    </div>
                    {/* Responsive button block; stack below 400px, row above */}
                    <div style={buttonBlockStyle}>
                        <Button
                            mode="outlined"
                            type="button"
                            onClick={handleCancel}
                            style={{
                                width: isMobileStack ? '100%' : 'auto',
                                marginBottom: isMobileStack ? '13px' : '0'
                            }}
                        >Cancel</Button>
                        <Button
                            mode="primary-bold"
                            type="submit"
                            style={{
                                width: isMobileStack ? '100%' : 'auto'
                            }}
                        >Create User</Button>
                    </div>
                </form>
                {/* Card accent bar */}
                <div
                    style={{
                        height: 8,
                        width: '100%',
                        borderBottomLeftRadius: theme.cardRadius,
                        borderBottomRightRadius: theme.cardRadius,
                        background: theme.gradientBackground,
                        position: 'absolute',
                        left: 0,
                        bottom: 0
                    }}
                />
            </div>
        </div>
    );
}

export default CreateApplicationUserComponent;